import { z } from 'zod';

export const DEFAULT_MEETING_WIDGET_SHORTCUT = 'control+option+m';

export const MeetingStatusWindowAppearanceSchema = z.object({
  appearance: z.enum(['expanded', 'collapsed']),
  focus: z.boolean().optional(),
});

export const MeetingWidgetShortcutTogglePayloadSchema = z.object({
  assistBadgeCount: z.number().int().nonnegative(),
});

export type MeetingWidgetShortcutTogglePayload = z.infer<
  typeof MeetingWidgetShortcutTogglePayloadSchema
>;

export type MeetingWidgetShortcutUpdateResult =
  | { success: true; shortcut: string }
  | {
      success: false;
      shortcut: string;
      reason: 'invalid' | 'unavailable' | 'registration-error';
    };

export enum EventType {
  Default = 'default',
  Birthday = 'birthday',
  FocusTime = 'focusTime',
  FromGmail = 'fromGmail',
  OutOfOffice = 'outOfOffice',
  WorkingLocation = 'workingLocation',
}

export const IGNORED_EVENT_TYPES = [
  EventType.WorkingLocation,
  EventType.OutOfOffice,
  EventType.Birthday,
  EventType.FocusTime,
];

/** Calendar event filters, sourced from settingsStore. Shared by the renderer's
 * backendApis.calendar.getEvents() and the Electron main process's menu-bar poll, so both
 * surfaces send the same query params to GET /calendar/events. */
export interface CalendarEventFilterSettings {
  daysAhead?: number;
  maxEvents?: number;
  excludeSoloEvents?: boolean;
  excludedEventTypes?: EventType[];
  excludedStatuses?: string[];
  includeAllDay?: boolean;
  timeMin?: string;
  timeMax?: string;
}

/** Converts filter settings into the flat boolean/scalar query params the backend's
 * CalendarEventFiltersQuery expects (one boolean per excludable type/status, not array params). */
export function buildCalendarEventFilterQueryParams(
  filters: CalendarEventFilterSettings
): Record<string, string | number | boolean | undefined> {
  const excludedTypes = new Set(filters.excludedEventTypes ?? []);
  const excludedStatuses = new Set(filters.excludedStatuses ?? []);
  return {
    days_in_future: filters.daysAhead,
    max_events: filters.maxEvents,
    exclude_solo_events: filters.excludeSoloEvents,
    exclude_working_location: excludedTypes.has(EventType.WorkingLocation),
    exclude_out_of_office: excludedTypes.has(EventType.OutOfOffice),
    exclude_birthday: excludedTypes.has(EventType.Birthday),
    exclude_focus_time: excludedTypes.has(EventType.FocusTime),
    exclude_from_gmail: excludedTypes.has(EventType.FromGmail),
    exclude_confirmed: excludedStatuses.has('confirmed'),
    exclude_tentative: excludedStatuses.has('tentative'),
    exclude_cancelled: excludedStatuses.has('cancelled'),
    include_all_day: filters.includeAllDay,
    time_min: filters.timeMin,
    time_max: filters.timeMax,
  };
}

export const CalendarEventAttendeeSchema = z.object({
  email: z.string().email().nullable().optional(),
  responseStatus: z.string().nullable().optional(),
  self: z.boolean().nullable().optional(),
  organizer: z.boolean().nullable().optional(),
  displayName: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  resource: z.boolean().nullable().optional(),
  optional: z.boolean().nullable().optional(),
});

const CalendarEventConferenceEntryPointSchema = z
  .object({
    entryPointType: z.string().nullable().optional(),
    uri: z.string().nullable().optional(),
  })
  .passthrough();

export const CalendarEventConferenceDataSchema = z.object({
  conferenceSolution: z
    .object({
      key: z.object({
        type: z.string(),
      }),
    })
    .optional(),
  entryPoints: z.array(CalendarEventConferenceEntryPointSchema).optional(),
  hasConference: z.boolean().optional(),
});

export const CalendarEventSchema = z.object({
  id: z.string(),
  status: z.enum(['confirmed', 'tentative', 'cancelled']).default('confirmed'),
  summary: z.string(),
  description: z.string().nullable().optional(),
  start_datetime: z.string().datetime({ offset: true }),
  end_datetime: z.string().datetime({ offset: true }),
  is_all_day: z.boolean().optional(),
  shows_as_free: z.boolean().optional(),
  htmlLink: z.string().url().nullable().optional(),
  attendees: z.array(CalendarEventAttendeeSchema).optional(),
  hangoutLink: z.string().url().nullable().optional(),
  conferenceData: CalendarEventConferenceDataSchema.optional(),
  eventType: z.nativeEnum(EventType).nullable().optional(),
  calendar_series_id: z.string().nullable().optional(),
});

export const CalendarEventsResponseSchema = z
  .object({ events: z.array(z.unknown()) })
  .transform(({ events }) => {
    const valid: z.infer<typeof CalendarEventSchema>[] = [];
    const validationErrors: Array<{ event: unknown; error: z.ZodError }> = [];
    for (const event of events) {
      const result = CalendarEventSchema.safeParse(event);
      if (result.success) valid.push(result.data);
      else validationErrors.push({ event, error: result.error });
    }
    return { events: valid, validationErrors };
  });

export type CalendarEvent = z.infer<typeof CalendarEventSchema>;
export type CalendarEventAttendee = z.infer<typeof CalendarEventAttendeeSchema>;

export const MeetingProviderSchema = z.enum([
  'hangoutsMeet',
  'zoomMeeting',
  'teamsForBusiness',
  'webexMeeting',
  'wherebyMeeting',
  'gotoMeeting',
  'slackHuddle',
]);

export type MeetingProvider = z.infer<typeof MeetingProviderSchema>;

export enum ShowMeetingStatusWindowOption {
  /** Internal debugging aid, not meant for external users; don't fix or QA issues that only show up in this mode */
  Always = 0,
  Automatic = 1,
}

export const MeetingStatusWindowDimensions = {
  CollapsedWidth: 32,
  CollapsedHeight: 63,
  ExpandedWidth: 640,
  ExpandedHeight: 580,
} as const;

/** The badge on the collapsed meeting widget for Meeting Assist cards still waiting for the user */
export const MeetingAssistBadgeSchema = z.object({
  count: z.number().int().min(0),
});

export type MeetingAssistBadge = z.infer<typeof MeetingAssistBadgeSchema>;

/** Room for the card at its longest and for its shadow */
export const MeetingAssistPopupWindowDimensions = {
  Width: 340,
  Height: 200,
  /** Space around the card inside the window, which its shadow draws into */
  CardInset: 8,
} as const;

/** A Meeting Assist card that pops up beside the collapsed meeting widget */
export const MeetingAssistPopupCardSchema = z.object({
  cardId: z.string(),
  title: z.string(),
  body: z.string(),
  displaySeconds: z.number().positive(),
  /** When the card passes its age limit (ms since epoch); a card still off screen then never shows */
  staleAt: z.number(),
});

export type MeetingAssistPopupCard = z.infer<
  typeof MeetingAssistPopupCardSchema
>;

/** The card the popup shows, or null to take it down */
export const MeetingAssistPopupSchema = z.object({
  card: MeetingAssistPopupCardSchema.nullable(),
});

export type MeetingAssistPopup = z.infer<typeof MeetingAssistPopupSchema>;

/**
 * What the popup's own window shows: its card, whether the window is on
 * screen, and where the pointer was in the window when it last showed, or null
 * when it was outside. The window hides with the pill, and its card's time on
 * screen waits
 */
export const MeetingAssistPopupViewSchema = MeetingAssistPopupSchema.extend({
  isShown: z.boolean(),
  pointer: z.object({ x: z.number(), y: z.number() }).nullable(),
});

export type MeetingAssistPopupView = z.infer<
  typeof MeetingAssistPopupViewSchema
>;

/** A card the popup showed on screen for the first time */
export const MeetingAssistPopupPresentationSchema = z.object({
  cardId: z.string(),
});

export type MeetingAssistPopupPresentation = z.infer<
  typeof MeetingAssistPopupPresentationSchema
>;

/**
 * How a popup ended: the user opened its card in the widget, closed it, or let
 * it time out, or the card passed its age limit before it could show
 */
export const MeetingAssistPopupResolutionSchema = z.object({
  cardId: z.string(),
  outcome: z.enum(['opened', 'closed', 'expired', 'skipped']),
});

export type MeetingAssistPopupResolution = z.infer<
  typeof MeetingAssistPopupResolutionSchema
>;

export enum MeetingNotificationFilter {
  ConfirmedOnly = 0,
  AllMeetings = 1,
}

export enum MeetingReminderLeadTime {
  OneMinute = 1,
  FiveMinutes = 5,
  TenMinutes = 10,
  ThirtyMinutes = 30,
  OneHour = 60,
}

export enum MeetingMenubarIncludeEventsOption {
  Today = 'today',
  TodayAndTomorrow = 'today-and-tomorrow',
  ThreeDays = '3-days',
  OneWeek = '1-week',
}

export enum MeetingMenubarPreviewLeadTimeOption {
  Never = 'never',
  AtStartOfEvent = 'at-start-of-event',
  ThirtyMinutes = '30-minutes',
  ThreeHours = '3-hours',
  Always = 'always',
}

const MEETING_MENUBAR_INCLUDE_EVENTS_LABELS: Record<
  MeetingMenubarIncludeEventsOption,
  string
> = {
  [MeetingMenubarIncludeEventsOption.Today]: 'Today',
  [MeetingMenubarIncludeEventsOption.TodayAndTomorrow]: 'Today & Tomorrow',
  [MeetingMenubarIncludeEventsOption.ThreeDays]: '3 days',
  [MeetingMenubarIncludeEventsOption.OneWeek]: '7 days',
};

const MEETING_MENUBAR_INCLUDE_EVENTS_DAYS: Record<
  MeetingMenubarIncludeEventsOption,
  number
> = {
  [MeetingMenubarIncludeEventsOption.Today]: 1,
  [MeetingMenubarIncludeEventsOption.TodayAndTomorrow]: 2,
  [MeetingMenubarIncludeEventsOption.ThreeDays]: 3,
  [MeetingMenubarIncludeEventsOption.OneWeek]: 7,
};

const MEETING_MENUBAR_PREVIEW_LEAD_TIME_LABELS: Record<
  MeetingMenubarPreviewLeadTimeOption,
  string
> = {
  [MeetingMenubarPreviewLeadTimeOption.Never]: 'Never',
  [MeetingMenubarPreviewLeadTimeOption.AtStartOfEvent]: 'At event start',
  [MeetingMenubarPreviewLeadTimeOption.ThirtyMinutes]: '30 mins before event',
  [MeetingMenubarPreviewLeadTimeOption.ThreeHours]: '3 hours before event',
  [MeetingMenubarPreviewLeadTimeOption.Always]: 'Always',
};

const MEETING_MENUBAR_PREVIEW_LEAD_TIME_MINUTES: Record<
  MeetingMenubarPreviewLeadTimeOption,
  number | null
> = {
  [MeetingMenubarPreviewLeadTimeOption.Never]: -1,
  [MeetingMenubarPreviewLeadTimeOption.AtStartOfEvent]: 0,
  [MeetingMenubarPreviewLeadTimeOption.ThirtyMinutes]: 30,
  [MeetingMenubarPreviewLeadTimeOption.ThreeHours]: 180,
  [MeetingMenubarPreviewLeadTimeOption.Always]: null,
};

export function getMeetingMenubarIncludeEventsLabel(
  option: MeetingMenubarIncludeEventsOption
): string {
  return MEETING_MENUBAR_INCLUDE_EVENTS_LABELS[option];
}

export function getMeetingMenubarIncludeEventsDays(
  option: MeetingMenubarIncludeEventsOption
): number {
  return MEETING_MENUBAR_INCLUDE_EVENTS_DAYS[option];
}

export function getMeetingMenubarPreviewLeadTimeLabel(
  option: MeetingMenubarPreviewLeadTimeOption
): string {
  return MEETING_MENUBAR_PREVIEW_LEAD_TIME_LABELS[option];
}

export function getMeetingMenubarPreviewLeadTimeMinutes(
  option: MeetingMenubarPreviewLeadTimeOption
): number | null {
  return MEETING_MENUBAR_PREVIEW_LEAD_TIME_MINUTES[option];
}

export enum MeetingAutoStopTimeout {
  Never = 0,
  FiveMinutes = 5,
  FifteenMinutes = 15,
}

export enum MeetingDetectionMode {
  DoNothing = 'do-nothing',
  Notify = 'notify',
  StartRecording = 'start-recording',
}

const MEETING_DETECTION_MODE_LABELS: Record<MeetingDetectionMode, string> = {
  [MeetingDetectionMode.DoNothing]: 'Do nothing',
  [MeetingDetectionMode.Notify]: 'Notify me',
  [MeetingDetectionMode.StartRecording]: 'Start recording',
};

export function getMeetingDetectionModeLabel(
  mode: MeetingDetectionMode
): string {
  return MEETING_DETECTION_MODE_LABELS[mode];
}

export function getMeetingProviderTitle(provider: MeetingProvider): string {
  switch (provider) {
    case 'hangoutsMeet':
      return 'Google Meet';
    case 'zoomMeeting':
      return 'Zoom';
    case 'teamsForBusiness':
      return 'Microsoft Teams';
    case 'webexMeeting':
      return 'Webex';
    case 'wherebyMeeting':
      return 'Whereby';
    case 'gotoMeeting':
      return 'GoToMeeting';
    case 'slackHuddle':
      return 'Slack';
    default:
      return 'Unknown';
  }
}

/** Regex to match meeting URLs from various providers */
const MEETING_LINK_REGEX =
  /https?:\/\/(?:[\w-]+\.)?(?:zoom\.us|meet\.google\.com|teams\.microsoft\.com|teams\.live\.com|webex\.com|whereby\.com|gotomeeting\.com)\/[^\s"<>]+/i;

function normalizeMeetingLink(url: string): string {
  return url.replace(/[.,;:!?)]+$/, '');
}

function getDescriptionMeetingLink(
  description: string | null | undefined
): string | undefined {
  if (!description) return undefined;
  const match = description.match(MEETING_LINK_REGEX);
  return match ? normalizeMeetingLink(match[0]) : undefined;
}

const HTTP_URL_REGEX = /^https?:\/\//i;

function getConferenceEntryPointMeetingLink(
  event: CalendarEvent
): string | undefined {
  const entryPoints = event.conferenceData?.entryPoints;
  if (!entryPoints) return undefined;

  let firstHttpUri: string | undefined;

  for (const entryPoint of entryPoints) {
    const uri = entryPoint.uri;
    if (!uri || !HTTP_URL_REGEX.test(uri)) continue;

    if (!firstHttpUri) firstHttpUri = uri;
    if (entryPoint.entryPointType?.toLowerCase() === 'video') return uri;
  }

  return firstHttpUri;
}

/**
 * Check if event is a solo event (marker / reminder rather than a real meeting).
 *
 * Treated as solo if:
 *   1. Zero or negative duration, or
 *   2. No guests other than the user.
 *
 * The user themselves (self=true) and booked rooms/resources (resource=true) don't count as guests —
 * Google frequently lists both, so a plain attendee count would keep genuinely solo events. Mirrors
 * the backend's exclude_solo_events filter in calendar_repository.py.
 */
export function isSoloEvent(event: CalendarEvent): boolean {
  const startMs = new Date(event.start_datetime).getTime();
  const endMs = new Date(event.end_datetime).getTime();
  if (endMs <= startMs) return true;

  const otherGuestCount =
    event.attendees?.filter((a) => !a.self && !a.resource).length ?? 0;
  return otherGuestCount < 1;
}

/**
 * Check if the current user has declined the meeting.
 */
export function isDeclinedByUser(event: CalendarEvent): boolean {
  const selfAttendee = event.attendees?.find((a) => a.self);
  return selfAttendee?.responseStatus === 'declined';
}

/**
 * Extract meeting link from event.
 * Priority: hangoutLink > conferenceData entryPoints > description link
 */
export function getMeetingLink(event: CalendarEvent): string | undefined {
  if (event.hangoutLink) return event.hangoutLink;

  const conferenceLink = getConferenceEntryPointMeetingLink(event);
  if (conferenceLink) return conferenceLink;

  return getDescriptionMeetingLink(event.description);
}

const ZOOM_HOST_PATTERN = /(^|\.)zoom\.us$/i;
const ZOOM_JOIN_PATH_PATTERN = /^\/(?:j|w)\/(\d+)\/?$/;
const ZOOM_TRANSLATABLE_PARAMS = ['pwd'];
const TEAMS_MEETUP_JOIN_PATH = '/l/meetup-join/';

export function getMeetingAppDeepLink(link: string): string | undefined {
  let parsed: URL;
  try {
    parsed = new URL(link);
  } catch {
    return undefined;
  }
  if (parsed.protocol !== 'https:') return undefined;

  if (ZOOM_HOST_PATTERN.test(parsed.hostname)) {
    const confno = parsed.pathname.match(ZOOM_JOIN_PATH_PATTERN)?.[1];
    if (!confno) return undefined;

    // Registrant tokens (tk) and anything else we can't translate must stay in
    // the browser — dropping them makes Zoom reject the join
    const hasUntranslatableParam = [...parsed.searchParams.keys()].some(
      (param) => !ZOOM_TRANSLATABLE_PARAMS.includes(param)
    );
    if (hasUntranslatableParam) return undefined;

    const deepLink = new URL(`zoommtg://${parsed.hostname}/join`);
    deepLink.searchParams.set('confno', confno);
    const pwd = parsed.searchParams.get('pwd');
    if (pwd) deepLink.searchParams.set('pwd', pwd);
    return deepLink.toString();
  }

  if (
    parsed.hostname.toLowerCase() === 'teams.microsoft.com' &&
    parsed.pathname.startsWith(TEAMS_MEETUP_JOIN_PATH)
  ) {
    return `msteams:${parsed.pathname}${parsed.search}`;
  }

  return undefined;
}

const GOOGLE_MEET_ROOM_PATTERN = /^[a-z]{3}-[a-z]{4}-[a-z]{3}$/;

/**
 * The room a calendar invite's conference link points at, for comparison
 * against the `meetingKey` of a browser segment event.
 *
 * Invites only ever carry the canonical join link, so this handles far less
 * than ContextKit's BrowserMeetingURLParser, which reads live browser tabs and
 * must also cope with `/wc/`, `/s/`, `/my/<alias>` and `lookup/<alias>`. Keep
 * the room formats themselves in sync with it.
 * @see apps/desktop/ContextKit/Sources/observer/lib/Meetings/BrowserMeetingDetection/BrowserMeetingIdentity.swift
 */
export function getMeetingRoomFromLink(
  link: string | null | undefined
): string | undefined {
  if (!link) return undefined;

  let parsed: URL;
  try {
    parsed = new URL(link);
  } catch {
    return undefined;
  }

  const host = parsed.hostname.toLowerCase();
  const segments = parsed.pathname.split('/').filter(Boolean);

  if (host === 'meet.google.com') {
    return segments
      .map((segment) => segment.toLowerCase())
      .find((segment) => GOOGLE_MEET_ROOM_PATTERN.test(segment));
  }

  if (ZOOM_HOST_PATTERN.test(host)) {
    return parsed.pathname.match(ZOOM_JOIN_PATH_PATTERN)?.[1];
  }

  return undefined;
}

const GOOGLE_URL_PATTERN =
  /^https?:\/\/(?:[\w-]+\.)?(?:google\.com|meet\.google\.com)\//i;

/**
 * Append ?authuser=<email> to Google URLs so Chrome opens them with the
 * correct account when multiple Google accounts are signed in.
 * Non-Google URLs are returned unchanged.
 */
export function withAuthUser(
  url: string | undefined,
  event: CalendarEvent
): string | undefined {
  if (!url) return url;
  if (!GOOGLE_URL_PATTERN.test(url)) return url;

  const selfEmail = event.attendees?.find((a) => a.self)?.email;
  if (!selfEmail) return url;

  const parsed = new URL(url);
  parsed.searchParams.set('authuser', selfEmail);
  return parsed.toString();
}

function getProviderFromLink(
  link: string | null | undefined
): MeetingProvider | undefined {
  if (!link) return undefined;
  const lowercaseLink = link.toLowerCase();

  if (lowercaseLink.includes('meet.google.com')) return 'hangoutsMeet';

  if (lowercaseLink.includes('zoom.us')) return 'zoomMeeting';

  if (
    lowercaseLink.includes('teams.microsoft.com') ||
    lowercaseLink.includes('teams.live.com')
  )
    return 'teamsForBusiness';

  if (lowercaseLink.includes('webex.com')) return 'webexMeeting';

  if (lowercaseLink.includes('whereby.com')) return 'wherebyMeeting';

  if (lowercaseLink.includes('gotomeeting.com')) return 'gotoMeeting';

  return undefined;
}

export function getMeetingProvider(
  event: CalendarEvent | null | undefined
): MeetingProvider | undefined {
  if (!event) return undefined;

  const rawType = event.conferenceData?.conferenceSolution?.key?.type;
  if (rawType === 'hangoutsMeet') return 'hangoutsMeet';

  return getProviderFromLink(getMeetingLink(event));
}

/**
 * One provider each from the Meet link, the conference data and the
 * description: an invite for a Zoom call often carries an auto-added Meet link.
 */
export function getMeetingProviders(event: CalendarEvent): MeetingProvider[] {
  const links = [
    event.hangoutLink,
    getConferenceEntryPointMeetingLink(event),
    getDescriptionMeetingLink(event.description),
  ];
  const providers = [
    getMeetingProvider(event),
    ...links.map(getProviderFromLink),
  ];
  return [...new Set(providers.filter((provider) => provider !== undefined))];
}

/**
 * Bundle ID → MeetingProvider mapping.
 *
 * Source: ContextKit MeetingAppDetector.fallbackBundles — keep in sync.
 * @see apps/desktop/ContextKit/Sources/observer/lib/Meetings/MeetingDetection/MeetingAppDetector.swift
 *
 * Browsers excluded — they can host any provider, so we fall back to getMeetingProvider().
 */
const BUNDLE_ID_TO_PROVIDER: Record<string, MeetingProvider> = {
  'us.zoom.xos': 'zoomMeeting',
  'us.zoom.videomeeting': 'zoomMeeting',
  'com.microsoft.teams2': 'teamsForBusiness',
  'com.microsoft.teams2.modulehost': 'teamsForBusiness',
  'com.microsoft.teams2.helper': 'teamsForBusiness',
  'com.microsoft.teams': 'teamsForBusiness',
  'com.tinyspeck.slackmacgap': 'slackHuddle',
  'com.tinyspeck.slackmacgap.helper': 'slackHuddle',
  'com.cisco.webexmeetingsapp': 'webexMeeting',
  'com.logmein.goto.meeting': 'gotoMeeting',
  'com.whereby.app': 'wherebyMeeting',
};

/** ContextKit-detected bundle ID → MeetingProvider (undefined for browsers). */
export function getProviderFromBundleId(
  bundleId: string | null | undefined
): MeetingProvider | undefined {
  return bundleId ? BUNDLE_ID_TO_PROVIDER[bundleId] : undefined;
}

/**
 * Segment-event provider name → MeetingProvider.
 *
 * Source: ContextKit BrowserMeetingIdentity.Provider raw values — keep in sync.
 * @see apps/desktop/ContextKit/Sources/observer/lib/Meetings/BrowserMeetingDetection/BrowserMeetingIdentity.swift
 */
const SEGMENT_PROVIDER_NAME_TO_PROVIDER: Record<string, MeetingProvider> = {
  'Google Meet': 'hangoutsMeet',
  Zoom: 'zoomMeeting',
};

export function getProviderFromSegmentProviderName(
  providerName: string | null | undefined
): MeetingProvider | undefined {
  return providerName
    ? SEGMENT_PROVIDER_NAME_TO_PROVIDER[providerName]
    : undefined;
}

export const UpcomingMeetingPayloadSchema = z.object({
  // Unique notification key in format "eventId:startTime" for deduplication
  key: z.string(),
  meetingId: z.string(),
  title: z.string(),
  startTime: z.string().datetime({ offset: true }),
  endTime: z.string().datetime({ offset: true }),
  link: z.string().url().nullable().optional(),
  provider: MeetingProviderSchema.optional(),
});

export const MeetingNotesUserSchema = z.object({
  id: z.union([z.string(), z.number()]).pipe(z.coerce.string()),
  calendar_connected: z.boolean().optional().default(false),
});

export const StartNoteTakingReasonSchema = z.enum([
  'manual',
  'calendar_event',
  'app_detection',
  'meeting_segment',
  'resume',
  'crash_recovery',
]);

export const MeetingTranscriptionInputSelectionSchema = z.discriminatedUnion(
  'type',
  [
    z.object({
      type: z.literal('system_default'),
    }),
    z.object({
      type: z.literal('specific_device'),
      deviceId: z.string().min(1),
    }),
  ]
);
export type MeetingTranscriptionInputSelection = z.infer<
  typeof MeetingTranscriptionInputSelectionSchema
>;

export const TranscriptionEngineSchema = z.enum(['remote', 'local']);
export type PreferredTranscriptionEngine = z.infer<
  typeof TranscriptionEngineSchema
>;
export type TranscriptionEngine = PreferredTranscriptionEngine;

export type MeetingTranscriptionEnginePreferenceSource =
  | 'default_policy'
  | 'manual';

const LocalTranscriptionModelInfoSchema = z.object({
  id: z.literal('parakeet-v3'),
  displayName: z.literal('Parakeet v3'),
});

export const LocalTranscriptionReadinessSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('not_started'),
    model: LocalTranscriptionModelInfoSchema,
  }),
  z.object({
    state: z.literal('preparing'),
    model: LocalTranscriptionModelInfoSchema,
    phase: z.enum(['downloading', 'loading']),
    startedAt: z.string().datetime({ offset: true }),
    progress: z
      .object({
        completedBytes: z.number().nonnegative().optional(),
        totalBytes: z.number().positive().optional(),
        fraction: z.number().min(0).max(1).optional(),
        completedFiles: z.number().nonnegative().optional(),
        totalFiles: z.number().positive().optional(),
      })
      .optional(),
  }),
  z.object({
    state: z.literal('cache_ready'),
    model: LocalTranscriptionModelInfoSchema,
    preparedAt: z.string().datetime({ offset: true }),
  }),
  z.object({
    state: z.literal('disk_cached'),
    model: LocalTranscriptionModelInfoSchema,
    preparedAt: z.string().datetime({ offset: true }),
  }),
  z.object({
    state: z.literal('resident_ready'),
    model: LocalTranscriptionModelInfoSchema,
    preparedAt: z.string().datetime({ offset: true }),
  }),
  z.object({
    state: z.literal('ready'),
    model: LocalTranscriptionModelInfoSchema,
    preparedAt: z.string().datetime({ offset: true }),
  }),
  z.object({
    state: z.literal('failed'),
    model: LocalTranscriptionModelInfoSchema,
    code: z.enum([
      'download_failed',
      'compile_failed',
      'load_failed',
      'unknown',
    ]),
    message: z.string(),
    failedAt: z.string().datetime({ offset: true }),
    retryable: z.boolean(),
  }),
  z.object({
    state: z.literal('unavailable'),
    model: LocalTranscriptionModelInfoSchema,
    code: z.enum(['not_compiled_in', 'unsupported_runtime', 'unknown']),
    message: z.string(),
    retryable: z.literal(false),
  }),
]);

export type LocalTranscriptionReadiness = z.infer<
  typeof LocalTranscriptionReadinessSchema
>;

/** Payload for start note-taking signal from FE. Contains meetingId created on backend. */
export const StartNoteTakingEventPayloadSchema = z.object({
  meetingId: z.string().uuid(),
  appBundleId: z.string().optional(),
  startTrigger: StartNoteTakingReasonSchema.optional(),
  detectionId: z.string().optional(),
  autoStartAttemptId: z.string().optional(),
  notificationId: z.string().optional(),
  transcriptionInputSelection:
    MeetingTranscriptionInputSelectionSchema.optional(),
  preferredTranscriptionEngine: TranscriptionEngineSchema.optional(),
  /** Meeting-level vocabulary terms (attendee names etc.) for transcription biasing. */
  customVocabulary: z.array(z.string()).optional(),
});

export type StartNoteTakingPayload = z.infer<
  typeof StartNoteTakingEventPayloadSchema
>;

/** Semantic fields describe the meeting; preserved across retry. */
export const AppDetectionSemanticFieldsSchema = z.object({
  appName: z.string().optional(),
  appBundleId: z.string().optional(),
  provider: MeetingProviderSchema.optional(),
  meetingKey: z.string().optional(),
  detectionId: z.string().optional(),
});

/** Attempt fields identify this particular try; stripped or regenerated on retry. */
export const AppDetectionAttemptFieldsSchema = z.object({
  autoStartAttemptId: z.string().optional(),
  notificationId: z.string().optional(),
  startAction: z
    .enum([
      'timer_auto_start',
      'auto_start_without_notification',
      'user_start_recording_click',
    ])
    .optional(),
});

export const AppDetectionStartInputSchema = z
  .object({ startReason: z.literal('app_detection') })
  .merge(AppDetectionSemanticFieldsSchema)
  .merge(AppDetectionAttemptFieldsSchema);

const ResumeSourceSchema = z.enum([
  'notification',
  'meeting_page',
  'shortcut',
  'record_here',
  'contextkit_recovery',
]);

/**
 * Input payload for starting a meeting (from the "meeting detected" window etc to -> main window IPC).
 * Discriminated union on `startReason` makes caller intent explicit.
 */
export const StartMeetingInputSchema = z.discriminatedUnion('startReason', [
  // User clicked "New Note" manually — always creates a new meeting
  z.object({
    startReason: z.literal('manual'),
    appBundleId: z.string().optional(),
    projectId: z.string().uuid().optional(),
  }),
  // Meeting app detected (Zoom, Meet, etc)
  AppDetectionStartInputSchema,
  // Starting from a calendar event (upcoming notification, etc)
  z.object({
    startReason: z.literal('calendar_event'),
    eventId: z.string(),
    title: z.string(),
    provider: MeetingProviderSchema.optional(),
    precedingRecordingStopReason: z.literal('new_meeting_started').optional(),
    precedingMeetingId: z.string().optional(),
  }),
  // The browser moved to a different meeting room while a recording was running
  z.object({
    startReason: z.literal('meeting_segment'),
    appName: z.string().optional(),
    appBundleId: z.string().optional(),
    provider: MeetingProviderSchema.optional(),
    meetingKey: z.string(),
    eventId: z.string().optional(),
    title: z.string().optional(),
    precedingMeetingId: z.string(),
    precedingRecordingStopReason: z.literal('new_meeting_started').optional(),
  }),
  // Resuming an existing meeting (pause/resume, crash recovery)
  z.object({
    startReason: z.literal('resume'),
    meetingId: z.string().uuid(),
    takeoverOtherDevice: z.boolean().optional(),
    resumeSource: ResumeSourceSchema.optional(),
  }),
]);

export type StartMeetingInput = z.infer<typeof StartMeetingInputSchema>;

export function startTriggerFor(
  input: StartMeetingInput
): z.infer<typeof StartNoteTakingReasonSchema> {
  return input.startReason === 'resume' &&
    input.resumeSource === 'contextkit_recovery'
    ? 'crash_recovery'
    : input.startReason;
}

export const MeetingCalendarEventRequestPayloadSchema = z.object({
  eventId: z.string(),
  title: z.string(),
});

export const MeetingMenubarCalendarStatusSchema = z.enum([
  'ready',
  'signed-out',
  'calendar-disconnected',
  'disabled',
  'error',
]);

export const MeetingMenubarCalendarEventSchema = z.object({
  id: z.string(),
  title: z.string(),
  startTime: z.string().datetime({ offset: true }),
  endTime: z.string().datetime({ offset: true }),
  status: z.enum(['confirmed', 'tentative']),
});

export type MeetingMenubarCalendarEvent = z.infer<
  typeof MeetingMenubarCalendarEventSchema
>;

export const MeetingMenubarCalendarConfigSchema = z.object({
  enabled: z.boolean(),
  chipLeadWindowMinutes: z.number().int().min(-1).nullable(),
  includeEventsWindowDays: z.number().int().positive(),
  maxMenuEvents: z.number().int().positive(),
});

export type MeetingMenubarCalendarConfig = z.infer<
  typeof MeetingMenubarCalendarConfigSchema
>;

export const MeetingMenubarCalendarDataSchema = z.object({
  status: MeetingMenubarCalendarStatusSchema,
  generatedAt: z.string().datetime({ offset: true }),
  events: z.array(MeetingMenubarCalendarEventSchema),
});

export type MeetingMenubarCalendarData = z.infer<
  typeof MeetingMenubarCalendarDataSchema
>;

export const MeetingAudioEnergyLevelsPayload = z.object({
  latestLevels: z.array(z.number()),
});

export const TranscriptDeltaPayloadSchema = z.object({
  speaker: z.string(),
  text: z.string(),
  isFinal: z.boolean(),
  chunkId: z.number(),
  supersededChunkIds: z.array(z.number()).default([]),
  /** Utterance-stream pipeline identity; absent on remote (Deepgram) deltas. */
  utteranceId: z.string().optional(),
});
export type TranscriptDeltaPayload = z.infer<
  typeof TranscriptDeltaPayloadSchema
>;

export const MeetingIntentTriggerSchema = z.enum([
  'send_email',
  'send_message',
  'create_ticket',
  'schedule_event',
]);

const MeetingIntentStreamSchema = z.enum(['own', 'others']);

export const MeetingIntentDetectedPayloadSchema = z.object({
  meetingId: z.string().uuid(),
  trigger: MeetingIntentTriggerSchema,
  stream: MeetingIntentStreamSchema,
  quote: z.string(),
  confidence: z.number(),
  prompt: z.string().min(1),
  detectedAt: z.coerce.date(),
});

export type MeetingIntentDetectedPayload = z.infer<
  typeof MeetingIntentDetectedPayloadSchema
>;
export type MeetingIntentTrigger = z.infer<typeof MeetingIntentTriggerSchema>;

export function getMeetingIntentKey({
  meetingId,
  trigger,
  detectedAt,
}: Pick<
  MeetingIntentDetectedPayload,
  'meetingId' | 'trigger' | 'detectedAt'
>): string {
  return `${meetingId}:${trigger}:${detectedAt.toISOString()}`;
}

export const MeetingIntentDetectedEventSchema = z
  .object({
    meeting_id: z.string().uuid(),
    intent: MeetingIntentTriggerSchema,
    stream: MeetingIntentStreamSchema,
    confidence: z.number(),
    quote: z.string(),
    prompt: z.string().min(1),
    detected_at: z.coerce.date(),
  })
  .transform((event): MeetingIntentDetectedPayload => ({
    meetingId: event.meeting_id,
    trigger: event.intent,
    stream: event.stream,
    quote: event.quote,
    confidence: event.confidence,
    prompt: event.prompt,
    detectedAt: event.detected_at,
  }));

export const MeetingIntentOverlayResolutionSchema =
  MeetingIntentDetectedPayloadSchema.extend({
    outcome: z.enum(['approved', 'dismissed', 'expired', 'superseded']),
  });

export type MeetingIntentOverlayResolution = z.infer<
  typeof MeetingIntentOverlayResolutionSchema
>;

export const MeetingIntentOverlayWindowDimensions = {
  Width: 420,
  Height: 112,
  TopInsetPx: 14,
} as const;

export const MeetingTranscriptionInputDeviceSchema = z.object({
  id: z.string(),
  name: z.string(),
  isAvailable: z.boolean(),
});

export const MeetingTranscriptionInputDevicesPayloadSchema = z.object({
  effectiveCurrentDevice: MeetingTranscriptionInputDeviceSchema.nullable(),
  selection: MeetingTranscriptionInputSelectionSchema,
  systemDefaultDeviceId: z.string().nullable(),
  availableDevices: z.array(MeetingTranscriptionInputDeviceSchema),
});
export type MeetingTranscriptionInputDevicesPayload = z.infer<
  typeof MeetingTranscriptionInputDevicesPayloadSchema
>;

export const MeetingPostProcessingStartPayloadSchema = z.object({
  meetingId: z.string().uuid(),
  /** Opaque round token the renderer sends and native echoes back on
   * state-changed/sync-state so a superseded round's result can be discarded.
   * Required: the Swift decoder requires it, so an optional TS type would let a
   * caller silently break native decode. */
  sessionEpoch: z.number().int(),
  /** Meeting-level custom vocabulary (e.g. attendee names). */
  customVocabulary: z.array(z.string()).optional(),
});
export type MeetingPostProcessingStartPayload = z.infer<
  typeof MeetingPostProcessingStartPayloadSchema
>;

export const MeetingRecoverablePostProcessingRecordingSchema = z.object({
  meetingId: z.string().uuid(),
});

export const MeetingRecoverablePostProcessingRecordingsPayloadSchema = z.object(
  {
    recordings: z.array(MeetingRecoverablePostProcessingRecordingSchema),
  }
);
export type MeetingRecoverablePostProcessingRecordingsPayload = z.infer<
  typeof MeetingRecoverablePostProcessingRecordingsPayloadSchema
>;

export const MeetingPostProcessingTranscriptSegmentPayloadSchema = z.object({
  speaker: z.string().min(1),
  text: z.string(),
});

export const MeetingPostProcessingTranscriptPayloadSchema = z.object({
  segments: z.array(MeetingPostProcessingTranscriptSegmentPayloadSchema),
});

export const MeetingPostProcessingCompletedPayloadSchema = z.object({
  transcript: MeetingPostProcessingTranscriptPayloadSchema,
});

export const MeetingPostProcessingFailedPayloadSchema = z.object({
  errorCode: z.string().min(1),
  /** Only the coordinator knows whether a failure (e.g. asr_failed) is worth
   * retrying, so it travels on the wire for the renderer to act on. */
  retryable: z.boolean(),
});

const MeetingPostProcessingBaseStateChangedPayloadSchema = z.object({
  meetingId: z.string().uuid(),
  /** Round token echoed verbatim from the start request; optional on the wire
   * during the placeholder phase, always populated by native. */
  sessionEpoch: z.number().int().optional(),
});

export const MeetingPostProcessingStateChangedPayloadSchema =
  z.discriminatedUnion('state', [
    MeetingPostProcessingBaseStateChangedPayloadSchema.extend({
      state: z.literal('completed'),
      completed: MeetingPostProcessingCompletedPayloadSchema,
    }),
    MeetingPostProcessingBaseStateChangedPayloadSchema.extend({
      state: z.literal('failed'),
      failed: MeetingPostProcessingFailedPayloadSchema,
    }),
  ]);
export type MeetingPostProcessingStateChangedPayload = z.infer<
  typeof MeetingPostProcessingStateChangedPayloadSchema
>;

export const MeetingPostProcessingJobReferencePayloadSchema = z.object({
  meetingId: z.string().uuid(),
});
export type MeetingPostProcessingJobReferencePayload = z.infer<
  typeof MeetingPostProcessingJobReferencePayloadSchema
>;

const MeetingPostProcessingBaseSyncStatePayloadSchema = z.object({
  meetingId: z.string().uuid(),
});

/** Reply to a sync-state request: how a reloaded renderer learns whether its
 * job is still running, finished while nobody listened (terminal payload
 * retained natively until acknowledged), or is gone entirely. */
export const MeetingPostProcessingSyncStatePayloadSchema = z.discriminatedUnion(
  'status',
  [
    MeetingPostProcessingBaseSyncStatePayloadSchema.extend({
      status: z.literal('running'),
      sessionEpoch: z.number().int().optional(),
    }),
    // `unknown` means the coordinator has no round to echo an epoch for.
    MeetingPostProcessingBaseSyncStatePayloadSchema.extend({
      status: z.literal('unknown'),
    }),
    MeetingPostProcessingBaseSyncStatePayloadSchema.extend({
      status: z.literal('completed'),
      sessionEpoch: z.number().int().optional(),
      completed: MeetingPostProcessingCompletedPayloadSchema,
    }),
    MeetingPostProcessingBaseSyncStatePayloadSchema.extend({
      status: z.literal('failed'),
      sessionEpoch: z.number().int().optional(),
      failed: MeetingPostProcessingFailedPayloadSchema,
    }),
  ]
);
export type MeetingPostProcessingSyncStatePayload = z.infer<
  typeof MeetingPostProcessingSyncStatePayloadSchema
>;

export const MeetingStoppedPayloadSchema = z.object({
  id: z.string().uuid().optional(),
  stopReason: z.string().optional(),
});
export type MeetingStoppedPayload = z.infer<typeof MeetingStoppedPayloadSchema>;

export const MeetingIdPayloadSchema = z.object({
  id: z.string().uuid(),
});
export type MeetingIdPayload = z.infer<typeof MeetingIdPayloadSchema>;

export const MeetingSegmentIdentitySchema = z.object({
  providerName: z.string(),
  meetingKey: z.string(),
});
export type MeetingSegmentIdentity = z.infer<
  typeof MeetingSegmentIdentitySchema
>;

export const MeetingAppGateAnswerSchema = z.enum([
  'meeting',
  'cant_tell',
  'not_meeting',
  'pending',
]);
export type MeetingAppGateAnswer = z.infer<typeof MeetingAppGateAnswerSchema>;

export const MeetingAppsUpdatedPayload = z.object({
  detectionId: z.string().optional(),
  apps: z.array(
    z.object({
      pid: z.number(),
      name: z.string(),
      bundleIdentifier: z.string().optional(),
      websiteHost: z.string().optional(),
      isBrowser: z.boolean().optional(),
      meetingIdentity: MeetingSegmentIdentitySchema.optional(),
      wasLiveWhenDetectionStarted: z.boolean().optional(),
      gateAnswer: MeetingAppGateAnswerSchema.optional(),
    })
  ),
});

/** Roster ContextKit read from the meeting app's window while a note is recording */
export const MeetingParticipantsUpdatedPayloadSchema = z.object({
  meetingId: z.string().uuid(),
  appBundleId: z.string().optional(),
  title: z.string(),
  participants: z.array(z.string()),
  selfParticipant: z.string().optional(),
});

export const MeetingSpeakerNameResolvedPayloadSchema = z.object({
  meetingId: z.string().uuid(),
  diarizedLabel: z.string(),
  speakerName: z.string(),
});

export const MeetingSegmentEventPayload = z.object({
  kind: z.enum(['joined', 'left', 'switched']),
  app: z.object({
    pid: z.number(),
    name: z.string(),
    bundleIdentifier: z.string().optional(),
  }),
  identity: MeetingSegmentIdentitySchema,
  previousIdentity: MeetingSegmentIdentitySchema.optional(),
});
export type MeetingSegmentEvent = z.infer<typeof MeetingSegmentEventPayload>;

export const MeetingInterruptReasonSchema = z.enum([
  'token_refresh_exhausted',
  'websocket_reconnect_exhausted',
  'audio_start_failed',
  'audio_start_exhausted',
  'audio_start_no_controller',
  'audio_start_invalid_state',
  'audio_start_permission_denied',
  'ipc_timeout',
  'silent_failure_exhausted',
  'meeting_quota_exceeded',
]);
export type MeetingInterruptReason = z.infer<
  typeof MeetingInterruptReasonSchema
>;

export const MeetingInterruptPayload = z.object({
  meetingId: z.string().uuid().nullish(),
  reason: MeetingInterruptReasonSchema,
});

export const MeetingStartInputSilentPayloadSchema = z.object({
  inputDeviceId: z.string(),
  inputDeviceName: z.string(),
  availableDevices: z.array(MeetingTranscriptionInputDeviceSchema),
});
export type MeetingStartInputSilentPayload = z.infer<
  typeof MeetingStartInputSilentPayloadSchema
>;

export const StopNoteTakingReasonSchema = z.enum([
  'stop_note_taking',
  'inactivity_timeout',
  'meeting_presence_lost',
  'sync_failure',
  'backend_create_failed',
  'meeting_limit_reached',
  'start_error',
  'contextkit-crash',
  'transcriber_interrupt',
  'audio_start_failed',
  'ipc_timeout',
  'new_meeting_started',
  'renderer_reload',
]);
export type StopNoteTakingReason = z.infer<typeof StopNoteTakingReasonSchema>;

export const MeetingRequestStopNotePayloadSchema = z
  .object({
    source: z.enum(['user', 'system']),
  })
  .optional();

export const StopNoteTakingInactivityTimeoutTelemetrySchema = z.object({
  timeoutMinutes: z.number().int().positive(),
  timeoutTriggeredAt: z.string().datetime(),
  lastTranscriptReceivedAt: z.string().datetime().nullish(),
  secondsSinceLastTranscriptAtTimeout: z.number().nonnegative().nullish(),
  lastSpeechAudioReceivedAt: z.string().datetime().nullish(),
  secondsSinceLastSpeechAudioAtTimeout: z.number().nonnegative().nullish(),
  transcriptGapSecondsAtTimeout: z.number().nullish(),
});
export type StopNoteTakingInactivityTimeoutTelemetry = z.infer<
  typeof StopNoteTakingInactivityTimeoutTelemetrySchema
>;

export const StopNoteTakingPresenceLostCauseSchema = z.enum([
  'no_apps_detected',
  'bound_app_missing',
]);
export type StopNoteTakingPresenceLostCause = z.infer<
  typeof StopNoteTakingPresenceLostCauseSchema
>;

export const StopNoteTakingPresenceLostTelemetrySchema = z.object({
  cause: StopNoteTakingPresenceLostCauseSchema,
  stopTriggeredAt: z.string().datetime(),
  detectedAppBundleIds: z.array(z.string()),
  secondsSinceLastTranscriptAtStop: z.number().nonnegative().nullish(),
  secondsSinceLastSpeechAudioAtStop: z.number().nonnegative().nullish(),
  scheduledEndAt: z.string().datetime().nullish(),
});
export type StopNoteTakingPresenceLostTelemetry = z.infer<
  typeof StopNoteTakingPresenceLostTelemetrySchema
>;

export type StopNoteTakingTelemetry = {
  inactivityTimeoutTelemetry?: StopNoteTakingInactivityTimeoutTelemetry;
  presenceLostTelemetry?: StopNoteTakingPresenceLostTelemetry;
};

export const StopNoteTakingRequestPayloadSchema = z.object({
  reason: StopNoteTakingReasonSchema,
  inactivityTimeoutTelemetry:
    StopNoteTakingInactivityTimeoutTelemetrySchema.optional(),
  presenceLostTelemetry: StopNoteTakingPresenceLostTelemetrySchema.optional(),
});

/**
 * Normalized window position (0.0-1.0) relative to display work area.
 * Enables proportional position translation across displays of different sizes.
 */
export interface NormalizedPosition {
  normalizedX: number;
  normalizedY: number;
}

export interface WorkArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const MeetingHeartbeatHealthSchema = z.object({
  isActive: z.boolean(),
  meetingId: z.string().nullish(),
  lastTranscriptionTimestamp: z.string().nullish(),
  secondsSinceLastTranscription: z.number().nullish(),
  transcriberState: z.string().nullish(),
});

export type MeetingHeartbeatHealth = z.infer<
  typeof MeetingHeartbeatHealthSchema
>;

/**
 * Convert absolute window position to normalized coordinates (0.0-1.0).
 * Normalized position represents where the window sits within the available
 * positioning range of the work area (accounting for window dimensions).
 */
export function toNormalizedPosition(
  x: number,
  y: number,
  windowWidth: number,
  windowHeight: number,
  workArea: WorkArea
): NormalizedPosition {
  const maxX = workArea.width - windowWidth;
  const maxY = workArea.height - windowHeight;

  // Avoid division by zero for edge cases where window fills the screen
  const normalizedX = maxX > 0 ? (x - workArea.x) / maxX : 0;
  const normalizedY = maxY > 0 ? (y - workArea.y) / maxY : 0;

  return {
    normalizedX: Math.max(0, Math.min(1, normalizedX)),
    normalizedY: Math.max(0, Math.min(1, normalizedY)),
  };
}

/**
 * Convert normalized position back to absolute coordinates for a given work area.
 * This enables proportional translation: "right-middle" on a 1920x1080 display
 * becomes "right-middle" on a 2560x1440 display.
 */
export function fromNormalizedPosition(
  pos: NormalizedPosition,
  windowWidth: number,
  windowHeight: number,
  workArea: WorkArea
): { x: number; y: number } {
  const maxX = workArea.width - windowWidth;
  const maxY = workArea.height - windowHeight;

  return {
    x: Math.round(workArea.x + pos.normalizedX * Math.max(0, maxX)),
    y: Math.round(workArea.y + pos.normalizedY * Math.max(0, maxY)),
  };
}

export const MEETING_NOTES_MIN_MACOS_VERSION = { major: 14, minor: 2 } as const;

// IPC handler parses each payload against this union and drops invalid ones as `telemetry_invalid`.

const MeetingAutoStartSettingsLoadedSchema = z.object({
  event_type: z.literal('settings_loaded'),
  meeting_auto_start_on_detection: z.boolean(),
  meeting_disable_detection_notification: z.boolean(),
});

const MeetingAutoStartSettingsChangedSchema = z.object({
  event_type: z.literal('settings_changed'),
  key: z.literal('meeting_auto_start_on_detection'),
  previous_value: z.boolean(),
  new_value: z.boolean(),
  trigger: z.enum(['user_toggle', 'sync', 'migration']),
});

const MeetingAutoStartCandidateDecisionSchema = z.object({
  event_type: z.literal('candidate_decision'),
  detection_id: z.string().nullable(),
  auto_start_attempt_id: z.string(),
  notification_id: z.string(),
  auto_start_setting_at_detection: z.boolean().nullable(),
  auto_start_setting_from_ref: z.boolean().nullable(),
  recording_status: z.string(),
  app_name: z.string(),
  app_bundle_id: z.string().nullable(),
  website_host: z.string().nullable(),
  candidate_count: z.number(),
  meeting_limit_reached: z.boolean(),
});

const MeetingAutoStartDispatchedSchema = z.object({
  event_type: z.literal('dispatched'),
  detection_id: z.string().nullable(),
  auto_start_attempt_id: z.string(),
  notification_id: z.string(),
  auto_start_dispatched: z.boolean().nullable(),
  app_bundle_id: z.string().nullable(),
});

const MeetingAutoStartNotificationResolvedSchema = z.object({
  event_type: z.literal('notification_resolved'),
  detection_id: z.string().nullable(),
  auto_start_attempt_id: z.string().nullable(),
  notification_id: z.string().nullable(),
  auto_start_in_payload: z.boolean().nullable(),
  resolution: z.enum([
    'timer_auto_start',
    'user_start_recording_click',
    'user_upgrade_click',
    'timer_dismiss',
    'user_dismiss',
    'exclude_app',
    'exclude_website',
  ]),
});

export const MeetingAutoStartTelemetrySchema = z.discriminatedUnion(
  'event_type',
  [
    MeetingAutoStartSettingsLoadedSchema,
    MeetingAutoStartSettingsChangedSchema,
    MeetingAutoStartCandidateDecisionSchema,
    MeetingAutoStartDispatchedSchema,
    MeetingAutoStartNotificationResolvedSchema,
  ]
);

export type MeetingAutoStartTelemetryEvent = z.infer<
  typeof MeetingAutoStartTelemetrySchema
>;

export type MeetingAutoStartEventType =
  MeetingAutoStartTelemetryEvent['event_type'];

/** Extract the metadata shape for a given event type (drops the discriminator). */
export type MeetingAutoStartMetadata<T extends MeetingAutoStartEventType> =
  Omit<
    Extract<MeetingAutoStartTelemetryEvent, { event_type: T }>,
    'event_type'
  >;

export const MeetingUnexpectedStopCauseSchema = z.enum([
  'lease_lost',
  'transcript_rejected',
  'auto_resume_failed',
  'interrupted',
]);

export type MeetingUnexpectedStopCause = z.infer<
  typeof MeetingUnexpectedStopCauseSchema
>;

/** Renderer events in the `meeting_recording_health` telemetry stream; no transcript text. */
export const MeetingRecordingHealthEventSchema = z.discriminatedUnion(
  'event_type',
  [
    z.object({
      event_type: z.literal('recording_unexpected_stop'),
      meeting_id: z.string().nullable(),
      cause: MeetingUnexpectedStopCauseSchema,
      interrupt_reason: MeetingInterruptReasonSchema.nullable(),
      app_focused: z.boolean(),
    }),
    z.object({
      event_type: z.literal('unsynced_speech_replay'),
      meeting_id: z.string(),
      outcome: z.enum(['replayed', 'failed']),
      interruption_reason: z.string(),
      unsynced_age_ms: z.number().int().nonnegative(),
      promotion_attempts: z.number().int().nonnegative(),
      pending_delta_count: z.number().int().nonnegative(),
    }),
    z.object({
      event_type: z.literal('native_start_refused_while_idle'),
      meeting_id: z.string().nullable(),
      start_reason: StartNoteTakingReasonSchema,
      refused_reason: z.literal('already_recording'),
    }),
  ]
);

export type MeetingRecordingHealthEvent = z.infer<
  typeof MeetingRecordingHealthEventSchema
>;
